import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Switch } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { FormikProps } from 'formik/dist/types';
import { ReactElement, SyntheticEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { imagesClient, sitesClient } from '../../api/client';
import { ImageRequestUpdateItem, QueryNearbySite } from '../../api/schema';
import { useLazyGetGeocodeQuery } from '../../apiOther/google';
import { postalCodeRegExp } from '../../helpers/dataHelper';
import useBooleanKeys from '../../hooks/useBooleanKeys';
import { useIsRemediation } from '../../hooks/useIsRemediation';
import { getAllCustomers, getCustomerListFetching, listCustomers } from '../../redux/slices/customer/customerSlice';
import { getImagesByOwnerId, listImagesByOwnerId } from '../../redux/slices/images/imagesSlice';
import { createSite, getSiteDetailsById, updateSite } from '../../redux/slices/sites/sitesSlice';
import { getAllUsers, getUserData, listUsers } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';
import { TextField } from '../../styled/inputs';
import { GetValidationErrors } from '../../tool/validation';
import { CustomerForm } from '../customer/CustomerForm';
import { UploadImage } from '../uploadImage/UploadImage';
import { AddressAutocomplete } from './AddressAutocomplete';
import { NearbyJobSitesDialog } from './NearbyJobSitesDialog';

interface IProp {
  siteId?: string;
  onClose: (redirectURL?: string) => void;
  open: boolean;
  type?: 'customer' | 'organization' | 'recycle';
  redirectTemplate?: string;
  organizationId?: string;
}

interface Values {
  id?: string;
  organization_id: string;
  name: string;
  customer_id?: string;
  address_1: string;
  city: string;
  state: string;
  postal: string;
  site_type_id: number | undefined;
  active: boolean;
  project_manager_id?: string;
  job_number?: string;
}

type ValuesToSend = Values & { latitude: string; longitude: string };

type AddressNames = {
  long_name: string;
  short_name: string;
};

type AddressType = {
  administrative_area_level_1?: AddressNames;
  administrative_area_level_2?: AddressNames;
  country?: AddressNames;
  locality?: AddressNames;
  postal_code?: AddressNames;
  postal_code_suffix?: AddressNames;
  route?: AddressNames;
  street_number?: AddressNames;
};

const schema = yup.object().shape({
  name: yup.string().min(2, 'Must be at least 2 characters').required('Required field'),
  address_1: yup.string().min(2, 'Must be at least 2 characters').required('Required field'),
  city: yup.string().required('Required field'),
  state: yup.string().required('Required field'),
  postal: yup.string().required('Required field').matches(postalCodeRegExp, 'Invalid Postal Code'),
});

// The API's error code for a job number already used within the organization.
const JOB_NUMBER_NOT_UNIQUE = 102700;

const urlByType = {
  customer: 'customers/job-sites',
  organization: 'company/sites',
  recycle: 'admin/sites/recycle',
};

const typeIdByType = {
  customer: 100,
  organization: 1,
  recycle: 1000,
};

const SiteForm = ({ onClose, siteId, open, type = 'customer', redirectTemplate, organizationId }: IProp): ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector(getUserData);
  const customers = useSelector(getAllCustomers({ organizationUuid: userData?.organization_id || '' }));
  const imageKeys = useSelector(getImagesByOwnerId(siteId));
  const site = useSelector(getSiteDetailsById(siteId));
  const customersFetching = useSelector(getCustomerListFetching);
  const [modals, toggleModal] = useBooleanKeys({ showCustomerForm: false });
  const isRemediation = useIsRemediation();
  const showProjectManager = type === 'customer';
  const users = useSelector(getAllUsers({ organizationId: userData?.organization_id || '' }));

  const formikRef = useRef<FormikProps<Values>>(null);
  const [image, setImage] = useState<ImageRequestUpdateItem | undefined>();
  const [nearbySites, setNearbySites] = useState<QueryNearbySite[]>([]);
  const [pendingValues, setPendingValues] = useState<ValuesToSend | null>(null);
  const [getGeocode] = useLazyGetGeocodeQuery();
  // Set when the user picks an address from autocomplete: it's already geocoded, so
  // we reuse its coordinates and skip the geocode round-trip on save. Any manual
  // edit of an address field clears it (invalidatePick) so we re-geocode on save.
  const pickedLatLng = useRef<{ lat: number; lng: number } | null>(null);
  const invalidatePick = useCallback(() => {
    pickedLatLng.current = null;
  }, []);

  useEffect(() => {
    if (userData?.organization_id) {
      dispatch(listCustomers({ organizationUuid: userData.organization_id }));
      if (showProjectManager) {
        // Keys must match the getAllUsers selector below (keysToId includes
        // every non-pagination key, so `active` would fork the cache); `limit`
        // is stripped by keysToId, so it's safe to fetch the full org list.
        dispatch(listUsers({ organizationId: userData.organization_id, limit: 1000 }));
      }
    }
  }, [dispatch, userData, showProjectManager]);

  const proceedAfterNearbyCheck = async (valuesToSend: ValuesToSend) => {
    if (siteId) {
      await dispatch(updateSite(valuesToSend));
    } else {
      const response = await dispatch(createSite(valuesToSend)).unwrap();
      siteId = response.data.result?.id;
    }

    if (image?.image_data !== undefined && siteId) {
      await imagesClient.deleteImageByOwnerId(siteId);
      if (image?.image_data) {
        image.id = siteId;
        await imagesClient.createImage(image);
      }
      await dispatch(listImagesByOwnerId(siteId));
    }

    onClose(redirectTemplate ? redirectTemplate.split('{siteId}').join(siteId) : `/${urlByType[type]}/${siteId}/details`);
  };

  // Puts a failed save on the form: a taken job number on the Job # field, and
  // anything else through the usual field:message mapping.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applySaveError = (e: any) => {
    const helpers = formikRef.current;
    if (e?.code === JOB_NUMBER_NOT_UNIQUE) {
      helpers?.setFieldTouched('job_number', true, false);
      helpers?.setFieldError('job_number', 'Job # is already used by another job site');
      return;
    }
    const status = e?.status ?? e?.response?.status;
    const messages = e?.messages ?? e?.response?.data?.messages ?? [];
    helpers?.setErrors(GetValidationErrors(status, navigate, messages));
  };

  // Geocode -> resolve lat/lng -> nearby check -> save. Called with the final
  // address (either what the user typed or an accepted Google suggestion). Uses
  // formikRef for submit/error state so it can run from submit or the dialog.
  const runSaveWithAddress = async (values: Values) => {
    const helpers = formikRef.current;
    try {
      helpers?.setSubmitting(true);

      let lat: number | undefined;
      let lng: number | undefined;
      const picked = pickedLatLng.current;
      if (picked) {
        // Chosen from autocomplete — already geocoded; skip the geocode round-trip.
        lat = picked.lat;
        lng = picked.lng;
      } else {
        const { data: geocode } = await getGeocode({ address: `${values.address_1},${values.city},${values.state},${values.postal}` }, false);
        const addressData = geocode?.[0]?.address_components?.reduce((partAddress, item) => {
          return { ...partAddress, [item.types[0]]: { long_name: item.long_name, short_name: item.short_name } };
        }, {} as AddressType);
        lat = geocode?.[0]?.geometry?.location?.lat;
        lng = geocode?.[0]?.geometry?.location?.lng;
        if (!addressData?.route?.long_name || lat === undefined || lng === undefined) {
          helpers?.setSubmitting(false);
          formikRef.current?.setErrors({ address_1: 'Can not resolve address' });
          return;
        }
      }

      const { project_manager_id: pmValue, job_number: jobNumber, ...restValues } = values;
      const valuesToSend: ValuesToSend = {
        ...restValues,
        latitude: lat?.toString() || '',
        longitude: lng?.toString() || '',
        ...(showProjectManager && pmValue ? { project_manager_id: pmValue } : {}),
        ...(!siteId && jobNumber?.trim() ? { job_number: jobNumber.trim() } : {}),
      };

      // Adjacent-jobsite check: only when creating a Job Site (type === 'customer'
      // and no siteId set). Skips edits and non-jobsite creates entirely.
      if (!siteId && type === 'customer' && lat !== undefined && lng !== undefined) {
        try {
          const nearbyResp = await sitesClient.listSitesNearby(lat, lng, typeIdByType[type]);
          const matches = nearbyResp.data.result || [];
          if (matches.length > 0) {
            setNearbySites(matches);
            setPendingValues(valuesToSend);
            helpers?.setSubmitting(false);
            return;
          }
        } catch (err) {
          // Non-fatal: if the proximity check fails (network blip, etc.) fall
          // through and create the site anyway. Better to allow create than
          // block on a transient error.
          console.log(err); // eslint-disable-line
        }
      }

      await proceedAfterNearbyCheck(valuesToSend);
      // eslint-disable-next-line
    } catch (e: any) {
      applySaveError(e);
    } finally {
      helpers?.setSubmitting(false);
    }
  };

  const submit = async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
    setSubmitting(true);
    values.organization_id = organizationId || userData?.organization_id || '';
    if (!values.customer_id) {
      delete values.customer_id;
    }
    // Address suggestions happen in the editor via autocomplete; save directly with
    // no confirm dialog. A picked address reuses its coordinates; a typed one is
    // geocoded in runSaveWithAddress.
    await runSaveWithAddress(values);
  };

  const handleClose = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );
  const handleCloseCustomerForm = useCallback(
    async (redirect?: string, customerId?: string) => {
      if (customerId && userData?.organization_id) {
        try {
          await dispatch(listCustomers({ organizationUuid: userData.organization_id }));
          formikRef.current?.setFieldValue('customer_id', customerId);
          toggleModal('showCustomerForm');
        } catch (error) {
          console.log(error); // eslint-disable-line
        }
      } else {
        toggleModal('showCustomerForm');
      }
    },
    [dispatch, userData, toggleModal],
  );

  const onUpload = (data: string, mimeType: string) => {
    setImage({ image_data: data, mime_type: mimeType });
  };

  const disabled = !(!siteId || (siteId && site?.id));

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
      {(!siteId || (siteId && site && !customersFetching)) && (
        <Formik
          innerRef={formikRef}
          validationSchema={schema}
          onSubmit={submit}
          initialValues={{
            ...(site ? { id: site.id } : {}),
            organization_id: organizationId || userData?.organization_id || '',
            name: site?.name || '',
            ...(type === 'customer' ? { customer_id: site?.customer_id || '' } : {}),
            address_1: site?.address1 || '',
            city: site?.city || '',
            state: site?.state || '',
            postal: site?.postal || '',
            site_type_id: site?.site_type_id || typeIdByType[type],
            active: site?.active !== undefined ? site?.active : true,
            ...(showProjectManager ? { project_manager_id: site?.project_manager_id || '' } : {}),
            ...(!site && type === 'customer' ? { job_number: '' } : {}),
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            // isValid,
            errors,
            isSubmitting,
            setValues,
            setFieldValue,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogTitle>{site?.id ? 'Edit Site' : 'Create Site'}</DialogTitle>
              <DialogContent>
                <Grid container spacing={6}>
                  <Grid item xs={12} md={6}>
                    <Grid container>
                      <Grid item xs={12}>
                        <TextField
                          name="name"
                          label="Name"
                          value={values.name}
                          error={Boolean(touched.name && errors.name)}
                          fullWidth
                          helperText={touched.name && errors.name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          variant="outlined"
                          my={2}
                          required
                        />
                        {!site?.id && type === 'customer' && (
                          <TextField
                            name="job_number"
                            label="Job # (optional)"
                            value={values.job_number ?? ''}
                            error={Boolean(touched.job_number && errors.job_number)}
                            fullWidth
                            helperText={touched.job_number && errors.job_number}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            variant="outlined"
                            my={2}
                          />
                        )}
                        {type === 'customer' && !isRemediation && (
                          <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs>
                              <TextField
                                select
                                name="customer_id"
                                label="Customer"
                                error={Boolean(touched.customer_id && errors.customer_id)}
                                fullWidth
                                value={values.customer_id}
                                helperText={touched.customer_id && errors.customer_id}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                variant="outlined"
                                my={2}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton edge="end" sx={{ mr: 4 }} onClick={() => setValues({ ...values, customer_id: '' })}>
                                        <CloseIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              >
                                {(customers.result &&
                                  customers.result.map((customer) => (
                                    <MenuItem key={customer.id} value={customer.id}>
                                      {customer.name}
                                    </MenuItem>
                                  ))) ||
                                  []}
                              </TextField>
                            </Grid>
                            <Grid item>
                              <Button
                                onClick={() => toggleModal('showCustomerForm')}
                                variant="contained"
                                color="primary"
                                disabled={customersFetching}
                                sx={{ mt: 2, mb: 2, width: 85 }}
                              >
                                Create
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <AddressAutocomplete
                          name="address_1"
                          label="Address"
                          value={values.address_1}
                          error={Boolean(touched.address_1 && errors.address_1)}
                          helperText={touched.address_1 && errors.address_1}
                          onBlur={handleBlur}
                          required
                          onInputChange={(text) => {
                            setFieldValue('address_1', text);
                            // Free typing after a pick invalidates the picked geocode.
                            invalidatePick();
                          }}
                          onPlaceSelected={(place) => {
                            setValues({
                              ...values,
                              address_1: place.address_1,
                              city: place.city,
                              state: place.state,
                              postal: place.postal,
                            });
                            pickedLatLng.current = { lat: place.lat, lng: place.lng };
                          }}
                        />
                      </Grid>
                      <Grid item xs sx={{ pr: 2 }}>
                        <TextField
                          name="city"
                          label="City"
                          value={values.city}
                          error={Boolean(touched.city && errors.city)}
                          fullWidth
                          helperText={touched.city && errors.city}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            handleChange(e);
                            invalidatePick();
                          }}
                          variant="outlined"
                          my={2}
                          required
                        />
                      </Grid>
                      <Grid item xs sx={{ pl: 2, pr: 2 }}>
                        <TextField
                          name="state"
                          label="State"
                          value={values.state}
                          error={Boolean(touched.state && errors.state)}
                          fullWidth
                          helperText={touched.state && errors.state}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            handleChange(e);
                            invalidatePick();
                          }}
                          variant="outlined"
                          my={2}
                          required
                        />
                      </Grid>
                      <Grid item xs sx={{ pl: 2 }}>
                        <TextField
                          name="postal"
                          label="Postal"
                          value={values.postal}
                          error={Boolean(touched.postal && errors.postal)}
                          fullWidth
                          helperText={touched.postal && errors.postal}
                          onBlur={handleBlur}
                          onChange={(e) => {
                            handleChange(e);
                            invalidatePick();
                          }}
                          variant="outlined"
                          my={2}
                          required
                        />
                      </Grid>
                    </Grid>
                    {showProjectManager && (
                      <Grid container spacing={1} alignItems="center" wrap="nowrap" sx={{ mt: 2 }}>
                        <Grid item xs>
                          <TextField
                            select
                            name="project_manager_id"
                            label="Project Manager"
                            fullWidth
                            value={values.project_manager_id ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            variant="outlined"
                            my={2}
                          >
                            {(users?.result ?? []).map((user) => (
                              <MenuItem key={user.id} value={user.id}>
                                {`${user.last_name}, ${user.first_name}`}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item>
                          <IconButton
                            aria-label="Clear project manager"
                            disabled={!values.project_manager_id}
                            onClick={() => setFieldValue('project_manager_id', '')}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    )}
                    {site?.id && (
                      <FormControlLabel control={<Switch checked={values.active} onChange={handleChange} name="active" onBlur={handleBlur} />} label="Active" />
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <UploadImage width="100%" imageKey={imageKeys && imageKeys.length > 0 ? imageKeys[0] : undefined} onUpload={onUpload} disabled={disabled} />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 6, pb: 6 }}>
                <Button onClick={handleClose} color="primary">
                  Cancel
                </Button>
                <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                  {site?.id ? 'Save' : 'Create'}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      )}
      <CustomerForm open={modals.showCustomerForm} onClose={handleCloseCustomerForm} />
      <NearbyJobSitesDialog
        open={nearbySites.length > 0}
        sites={nearbySites}
        onPickExisting={(existingId) => {
          setNearbySites([]);
          setPendingValues(null);
          onClose(redirectTemplate ? redirectTemplate.split('{siteId}').join(existingId) : `/${urlByType[type]}/${existingId}/details`);
        }}
        onContinueCreate={async () => {
          if (pendingValues) {
            const toCreate = pendingValues;
            setNearbySites([]);
            setPendingValues(null);
            try {
              await proceedAfterNearbyCheck(toCreate);
            } catch (err) {
              applySaveError(err);
            }
          }
        }}
        onClose={() => {
          setNearbySites([]);
          setPendingValues(null);
        }}
      />
    </Dialog>
  );
};

export default memo(SiteForm);
